/**
 * MobileHeader — the FIXED, unscrollable zone at the top of the mobile
 * shell (capped at 30dvh; see docs/plans/2026-07-06_MOBILE_HEADER_HOME.md).
 * Carries what the desktop taskbar rail + sidebar carry, since phones have
 * neither: brand (which opens the app-switcher launcher), notification bell,
 * theme toggle, the CURRENT-VIEW icon (drive/workspace/keybook — also opens
 * the launcher), and the "Recent" carousel. Account controls (switch /
 * create org / sign out) live in the launcher's account sheet. Content
 * below is the only vertical scroller.
 */
import { useRef, useState } from 'react'
import { BrandLogo, useBrand } from '@papan/brand/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Eclipse, Globe, Menu, Moon, Settings, Sun, Gauge } from 'lucide-react'
import { NotificationBell, useTheme, MiniappIcon, useBudgetBillingAccess } from '@papan/core-ui/ui'
import { useRealtime } from '@papan/realtime/client'
import { orgsApi } from '@papan/orgs/client'
import { workspaceApi, getWorkspaceName } from '@papan/workspace/client'
import { useFile } from '@papan/files/client/queries'
import { presentsAsMiniapp, parseMiniappIcon, appDisplayName } from '@papan/types'
import { useTenant } from '../hooks/useTenant'
import { useAuth } from '../hooks/useAuth'
import { MobileRecents } from './MobileRecents'
import { MobileNavOverlay } from './MobileNavOverlay'
import { MobileWorkspaceSwitcher } from './MobileWorkspaceSwitcher'
import { MobileDriveSectionMenu, useDriveSections } from './MobileDriveSectionMenu'
import { useAppPanel } from './appPanel'
import { useMobileTabs, activeTab } from './nav'
import { useIdentityOnly } from './useIdentityOnly'

function greetingForHour(h: number): string {
  if (h < 5) return 'Up late'
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

// The logo mark was a verbatim copy of TaskBar's inlined prism (both hardcoded
// to `#AD46FF`). Both now use `<BrandLogo />` from @papan/brand — one glyph,
// one colour source, no copies left to drift.

export function MobileHeader() {
  const location = useLocation()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const { onNotification } = useRealtime()
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [wsSwitcherOpen, setWsSwitcherOpen] = useState(false)
  const [driveMenuOpen, setDriveMenuOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const appPanel = useAppPanel()
  const closeMenu = () => setMenuOpen(false)

  // Theme row in the burger — cycles light → dim → dark, honoring the syncAll
  // rule (same as ThemeToggle / the Settings page). The icon shows the current
  // mode.
  const { settings, updateSetting } = useTheme()
  const themeMode = settings.theme.workspace
  const ThemeIcon = themeMode === 'light' ? Sun : themeMode === 'dim' ? Eclipse : Moon
  const cycleTheme = () => {
    const next = ({ light: 'dim', dim: 'dark', dark: 'light' } as const)[themeMode]
    if (settings.theme.syncAll) updateSetting('theme', { ...settings.theme, drive: next, workspace: next, chat: next })
    else updateSetting('theme', { ...settings.theme, workspace: next })
  }
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  const { productName } = useBrand()
  // useMobileTabs(), not the raw const: `current.label` is DISPLAYED below and
  // the chat tab's label is the brand product name.
  const mobileTabs = useMobileTabs(useIdentityOnly())
  // `activeKey` is where you ARE; `current` is the tab whose label/icon we
  // render, which needs a fallback because a page can exist without a tab —
  // `/…/launcher` on a chat-landing build routes but is not advertised in the
  // strip (nav.tsx). Keep the two apart: deciding the SPECIAL chrome below off
  // `current` means that fallback silently claims the page, and a launcher
  // opened on a phone painted the chat home's serif greeting over an app grid.
  const activeKey = activeTab(location.pathname)
  const current = mobileTabs.find((t) => t.key === activeKey) ?? mobileTabs[0]
  const CurrentIcon = current.icon
  // Settings sits outside the top-level tabs (activeTab falls back to 'drive'
  // for it), so detect it explicitly and don't treat it as the drive.
  const onSettings = /\/settings(\/|$)/.test(location.pathname)
  // Running a `.miniapp` (/app/:id): the cutout becomes the app's own identity
  // (icon · MINIAPP · title). activeTab() has no 'app' bucket — it falls back to
  // 'drive' — so detect the route explicitly and exclude it from onDrive (else
  // it'd steal the drive label AND leak the recents strip onto the app).
  const appFileId = location.pathname.match(/\/app\/([^/?]+)/)?.[1]
  const onApp = !!appFileId
  const { data: appInfo } = useFile(appFileId)
  const appIsMiniapp = !!appInfo?.name && presentsAsMiniapp(appInfo.name, appInfo.fileType, appInfo.isFolder)
  const appTitle = appInfo?.name ? appDisplayName(appInfo.name) : 'App'
  const appIconSpec = appInfo ? parseMiniappIcon(appInfo.metadataJson) : null
  // On the drive, the cutout drops the section menu (Home · My Drive ·
  // Projects · Shared) instead of the app launcher — the launcher stays one
  // tap away on the logo mark.
  const onDrive = activeKey === 'drive' && !onSettings && !onApp
  // Chat home: the cutout pill is replaced by the landing's serif greeting
  // (no dropdown) — navigation stays one tap away on the logo mark.
  const onChat = activeKey === 'chat' && !onSettings
  const { user } = useAuth()
  // The section list is the same cached hook the dropdown uses (no fetch).
  const driveSections = useDriveSections()

  // Same source as the desktop taskbar's tenant switcher (App.tsx keeps it
  // in component state; here TanStack caches it under the mobile key).
  const tenantsQuery = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })
  const tenants = tenantsQuery.data?.tenants ?? []
  const tenantName = tenants.find((t) => t.id === tenantId)?.name

  // Usage & Billing entry — the PLATFORM shelf, so this is a platform admin
  // acting in the platform org and nobody else. An org admin's budget pages
  // live in Settings → Organization now, which this menu already links to.
  //
  // Only `allowed` matters here: this is a nav item, and hiding it during any
  // in-flight fetch is the right default (the route guard, which must not
  // conflate loading with denial, reads `loading` instead).
  const { allowed: hasBudgetBilling } = useBudgetBillingAccess({
    tenantId, platformRole: user?.platformRole,
  })

  // Cutout label on the drive: line 1 = whose drive (user's first name when
  // personal, org name in an org), line 2 = the current section.
  const driveFirstName = user?.name?.trim().split(/\s+/)[0]
  const driveOwnerLabel = isPersonal
    ? (driveFirstName ? `${driveFirstName}'s Drive` : 'My Drive')
    : (tenantName ? `${tenantName}'s Drive` : 'Drive')
  // Remember the last section that WAS active so a file view / deep subfolder
  // (which match no section) keeps showing where you came from — e.g. opening a
  // file from Home stays on "Home" instead of snapping to Library.
  const matchedSection = driveSections.find((s) => s.active)
  const lastSectionKey = useRef(matchedSection?.key ?? 'home')
  if (matchedSection) lastSectionKey.current = matchedSection.key
  const activeDriveSection = matchedSection
    ?? driveSections.find((s) => s.key === lastSectionKey.current)
    ?? driveSections[0]
  const driveSectionLabel = activeDriveSection?.label ?? current.label
  // The cutout icon tracks the current drive SECTION (Library/Projects/Shared/
  // My Space/Home) instead of the generic drive glyph.
  const DriveSectionIcon = activeDriveSection?.icon ?? CurrentIcon

  // Inside an OPEN workspace the brand label becomes a two-line context
  // label ("Workspace" over the workspace name). Name resolves from the
  // same list cache MobileWorkspaceList uses (shared key, no extra fetch
  // when already loaded).
  const wsFolderId = location.pathname.match(/\/workspace\/([^/?]+)/)?.[1]
  const workspacesQuery = useQuery({
    queryKey: ['mobile-workspaces', tenantId, activeOrgEmail ?? ''],
    queryFn: () => workspaceApi.list(),
    staleTime: 30_000,
    enabled: !!wsFolderId,
  })
  const wsName = wsFolderId
    ? (() => {
        const w = (workspacesQuery.data ?? []).find((w) => w.folderId === wsFolderId)
        return w ? getWorkspaceName(w.name) : undefined
      })()
    : undefined

  return (
    // NO overflow-hidden here: the bell's dropdown hangs BELOW the header's
    // box and would be clipped to nothing (the recents strip self-clips via
    // its own wrapper, so the 30dvh cap doesn't need it). z-[60] lifts the
    // header's popovers above the content region's z-50 bits (workspace
    // control-row pill/overview).
    <header className="relative z-[60] shrink-0 max-h-[30dvh] flex flex-col">
      {/* Row 1 — the Papan logo as a STANDALONE mark (launcher), then a
          LEFT-aligned carved-well CUTOUT pill wrapping the current-VIEW icon
          (drive / workspace / keybook) + the two-line context label. The
          label opens the workspace switcher inside a workspace, else the
          launcher. Bell + burger on the right. */}
      <div className="relative flex items-center gap-2 px-4 pt-2 pb-1 shrink-0">
        <button
          onClick={() => setSwitcherOpen(true)}
          className="flex shrink-0 items-center rounded-lg -ml-1 px-1 py-0.5 active:themed-bg-tertiary"
          aria-label="Open navigation"
          aria-haspopup="menu"
          aria-expanded={switcherOpen}
        >
          {/* decorative: the button is already labelled "Open navigation". */}
          <BrandLogo decorative className="w-6 h-6 shrink-0" />
        </button>
        <div className="relative ml-2 flex min-h-[57px] min-w-0 flex-1 items-center">
          {onChat && !wsFolderId ? (
            /* Chat home: no cutout pill / dropdown — the greeting (+ org name)
               is a SCREEN-centered overlay on the whole header row (below), so
               this slot is just the spacer that pushes bell/burger right. The
               min-h on the container keeps the row height stable across tabs. */
            null
          ) : (
          <button
            onClick={() => {
              if (wsFolderId) setWsSwitcherOpen(true)
              else if (onDrive) setDriveMenuOpen((o) => !o)
              else if (onApp) appPanel.toggle()
              else setSwitcherOpen(true)
            }}
            className="mobile-hdr-cutout flex min-h-[57px] w-full min-w-0 items-center gap-3 rounded-xl py-1.5 pl-2 pr-2.5 text-left active:opacity-80"
            aria-label={wsFolderId ? 'Switch workspace' : onSettings ? 'Settings — open navigation' : onDrive ? 'Drive sections' : onApp ? 'App controls' : `Current app: ${current.label} — open navigation`}
            aria-haspopup="menu"
            aria-expanded={wsFolderId ? wsSwitcherOpen : onDrive ? driveMenuOpen : onApp ? appPanel.open : switcherOpen}
          >
            {onApp ? (
              // Mini-apps show their squircle icon; standalone HTML (Mode A)
              // shows the Globe glyph — same split as the launcher tiles
              // (MobileNavOverlay) and the desktop dock.
              appIsMiniapp
                ? <MiniappIcon spec={appIconSpec} name={appTitle} size={30} className="ml-1.5 shrink-0" />
                : <Globe size={20} className="ml-1.5 shrink-0 text-purple-400" />
            ) : (() => {
              const CutoutIcon = onSettings ? Settings : wsFolderId ? CurrentIcon : onDrive ? DriveSectionIcon : CurrentIcon
              return <CutoutIcon size={20} className="ml-1.5 shrink-0 text-purple-400" />
            })()}
            <span className="min-w-0 flex flex-col leading-tight">
              <span className="min-w-0 truncate text-[10px] uppercase tracking-wide themed-text-muted">
                {/* Settings on a personal tenant has no org name to show, so it
                    falls back to the product name — brand-derived, not literal. */}
                {onApp ? (appIsMiniapp ? 'PDApp' : 'HTML') : onSettings ? (isPersonal || !tenantName ? productName : tenantName) : wsFolderId ? 'Project' : onDrive ? driveOwnerLabel : current.label}
              </span>
              <span className="min-w-0 truncate text-sm font-semibold themed-text">
                {/* Same brand-derived fallback as the line above. It was left
                    literal while its sibling was converted, so a branded host
                    showed its own name on one line and "Papan" on the next. */}
                {onApp ? appTitle : onSettings ? 'Settings' : wsFolderId ? (wsName ?? '…') : onDrive ? driveSectionLabel : (isPersonal || !tenantName ? productName : tenantName)}
              </span>
            </span>
            <ChevronDown size={14} className="ml-auto shrink-0 themed-text-muted" />
          </button>
          )}
          {driveMenuOpen && onDrive && (
            <MobileDriveSectionMenu ownerLabel={driveOwnerLabel} onClose={() => setDriveMenuOpen(false)} />
          )}
        </div>
        {/* Read-only on mobile for now: no onAction — rows show but nothing is
            clickable (Phase 3 of the mobile plan).

            This used to say the gap was only the desktop approval popups. It is
            wider than that now: `budget_threshold` carries a plain NAVIGATION
            action (Settings → Budget), so mobile readers of a spend alert also
            get copy with no way to act on it.

            Do NOT close it by passing a handler that covers only the navigation
            types. `NotificationBell` derives clickability from `onAction` being
            present AT ALL, so a partial handler turns every action-bearing row
            — share approvals included — into a click that does nothing, which
            is worse than a row that never invited the tap. The fix is the whole
            handler, which needs the popup infrastructure Phase 3 brings. */}
        <NotificationBell placement="mobile" onSubscribe={onNotification} />
        {/* Burger — Settings + theme live here (no standalone theme icon). */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 items-center justify-center rounded-lg themed-icon active:themed-bg-tertiary"
          >
            <Menu size={19} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={closeMenu} aria-hidden />
              {/* Chrome-style menu — frosted .ws-menu kit; icon · label rows
                  with hover. */}
              <div className="ws-menu absolute right-0 top-full z-50 mt-1.5 w-56 p-1">
                <button
                  onClick={() => { closeMenu(); navigate(`${prefix}/settings`) }}
                  className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm themed-text hover:themed-bg-tertiary active:themed-bg-tertiary"
                >
                  <Settings size={16} className="shrink-0 themed-icon" />
                  <span className="flex-1">Settings</span>
                </button>
                {/* Platform admins acting in the platform org only. Everyone
                    else's spend pages are in Settings: a member's own usage at
                    → Usage, an org's budget at → Organization. */}
                {hasBudgetBilling && (
                  <button
                    onClick={() => { closeMenu(); navigate(`${prefix}/billing`) }}
                    className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm themed-text hover:themed-bg-tertiary active:themed-bg-tertiary"
                  >
                    <Gauge size={16} className="shrink-0 themed-icon" />
                    <span className="flex-1">Usage & Billing</span>
                  </button>
                )}
                {/* Theme — cycles light → dim → dark; icon shows current mode. */}
                <button
                  onClick={cycleTheme}
                  aria-label={`Theme: ${themeMode}. Tap to switch.`}
                  className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm themed-text hover:themed-bg-tertiary active:themed-bg-tertiary"
                >
                  <ThemeIcon size={16} className="shrink-0 themed-icon" />
                  <span className="flex-1">Theme</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Chat home: the greeting (+ org name above it, org tenants only) is
            a SCREEN-centered overlay — symmetric side insets clear the logo and
            the bell/burger, so it centers on the screen, not the off-center
            middle slot. pointer-events-none keeps those controls tappable
            through it. Desktop parity: serif greeting, org whisper above. */}
        {onChat && !wsFolderId && (
          <div className="pointer-events-none absolute inset-y-0 left-24 right-24 flex flex-col items-center justify-center gap-1.5 text-center">
            {!isPersonal && tenantName && (
              <span className="chat-home-orgname max-w-full truncate leading-tight" style={{ fontSize: 12.5 }}>
                {tenantName}
              </span>
            )}
            <span className="chat-home-greeting max-w-full truncate text-[19px] leading-tight themed-text">
              {greetingForHour(new Date().getHours())}{user?.name ? `, ${user.name.trim().split(/\s+/)[0]}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Recents carousel lives ONLY in the Drive view — everywhere else (chat
          home, workspace, settings, a running app) it's irrelevant clutter that
          eats vertical room. Still self-hides in landscape-height windows. */}
      {onDrive && (
        <div className="min-h-0 overflow-hidden [@media(max-height:500px)]:hidden">
          <MobileRecents />
        </div>
      )}

      {switcherOpen && (
        <MobileNavOverlay prefix={prefix} onClose={() => setSwitcherOpen(false)} />
      )}
      {wsSwitcherOpen && wsFolderId && (
        <MobileWorkspaceSwitcher activeFolderId={wsFolderId} onClose={() => setWsSwitcherOpen(false)} />
      )}
    </header>
  )
}
